import type { I18nFn } from '#/i18n/types/I18nFn';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { radiusStringToInt } from '#/modules/search/helpers/radius';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { getSearchRadiusDescription } from '#/modules/userDashboard/components/SavedItemCard/Search/getSearchRadiusDescription';
import { getCountryName, getUsStateName } from '#/utils/internationalization';

import { getMobileSearchNouns } from './getMobileSearchNouns';

export type MakeSearchLabelInput = {
  query?: string;
  location?: SearchLocation;
  radius?: string;
  // NOTE: false on the homepage / CMS pages
  showSearchContext: boolean;
};

type SearchLabel = { label: string; isPlaceholder: boolean };

const value = (label: string): SearchLabel => ({ label, isPlaceholder: false });
const placeholder = (label: string): SearchLabel => ({
  label,
  isPlaceholder: true,
});

const normalizeQuery = (query: string | undefined) =>
  query ? query.trim().replace(/\s+/g, ' ') : '';

const hasSearchInput = (
  query: string | undefined,
  location: SearchLocation | undefined,
): boolean => Boolean(normalizeQuery(query) || location?.text);

// Context-aware summary copy for the mobile/tablet search-bar button.
export function makeSearchLabel(
  i18n: I18nFn,
  locale: I18nLocale,
  type: SearchType,
  { query, location, radius, showSearchContext }: MakeSearchLabelInput,
): SearchLabel {
  /* TRANSLATORS: mobile search bar label on homepage / CMS pages */
  if (!showSearchContext) return placeholder(i18n('Volunteer or find a job'));

  const q = normalizeQuery(query);
  const { noun, Noun } = getMobileSearchNouns(i18n, type);

  const loc = location?.text ?? '';
  const hasLocation = Boolean(loc);

  // no query, no location -> placeholder
  if (!hasSearchInput(query, location))
    /* TRANSLATORS: mobile search placeholder, e.g. "Search jobs by keyword or location" */
    return placeholder(
      i18n('Search %(noun)s by keyword or location', { noun }),
    );

  // query only
  if (q && !hasLocation)
    /* TRANSLATORS: mobile search summary, query only, e.g. '"Manager" jobs' */
    return value(i18n('"%(query)s" %(noun)s', { query: q, noun }));

  if (radius === 'country') {
    const country = location?.countryCode
      ? getCountryName(i18n, location.countryCode)
      : '';
    if (country) {
      /* TRANSLATORS: mobile search summary, query + country */
      if (q)
        return value(
          i18n('"%(query)s" %(noun)s in %(country)s', {
            query: q,
            noun,
            country,
          }),
        );
      /* TRANSLATORS: mobile search summary, country */
      return value(i18n('%(Noun)s in %(country)s', { Noun, country }));
    }
    // else fall through to "near"
  }

  if (radius === 'state') {
    const state = location?.stateCode
      ? getUsStateName(i18n, location.stateCode)
      : '';
    if (state) {
      /* TRANSLATORS: mobile search summary, query + US state */
      if (q)
        return value(
          i18n('"%(query)s" %(noun)s in %(state)s State', {
            query: q,
            noun,
            state,
          }),
        );
      /* TRANSLATORS: mobile search summary, US state */
      return value(i18n('%(Noun)s in %(state)s State', { Noun, state }));
    }
    // else fall through to "near"
  }

  const isNumericRadius =
    Boolean(radius) && radius !== 'state' && radius !== 'country';
  if (isNumericRadius) {
    const distance = getSearchRadiusDescription(
      i18n,
      locale,
      radiusStringToInt(radius)!,
    );
    /* TRANSLATORS: mobile search summary, query + numeric radius */
    if (q)
      return value(
        i18n('"%(query)s" %(noun)s within %(distance)s of %(loc)s', {
          query: q,
          noun,
          distance,
          loc,
        }),
      );
    /* TRANSLATORS: mobile search summary, numeric radius */
    return value(
      i18n('%(Noun)s within %(distance)s of %(loc)s', {
        Noun,
        distance,
        loc,
      }),
    );
  }

  /* TRANSLATORS: mobile search summary, query + near location */
  if (q)
    return value(
      i18n('"%(query)s" %(noun)s near %(loc)s', { query: q, noun, loc }),
    );
  /* TRANSLATORS: mobile search summary, near location */
  return value(i18n('%(Noun)s near %(loc)s', { Noun, loc }));
}
